import React, { useState } from 'react';
import { 
  Container, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Stack,
  Card,
  CardContent,
  Chip,
  Divider,
  InputAdornment
} from '@mui/material';
import { Calculate, Info } from '@mui/icons-material';
import ResponsiveDiagram from './ResponsiveDiagram';

const CircWalkway: React.FC = () => {
  const [roughCut, setRoughCut] = useState('');
  const [radius, setRadius] = useState('');
  const [treadsIn90, setTreadsIn90] = useState<string | null>(null);
  const [treadAngle, setTreadAngle] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);
  const [calculatedInputs, setCalculatedInputs] = useState<string | null>(null);
  const currentInputs = JSON.stringify([roughCut, radius]);
  const isStale = calculatedInputs !== null && calculatedInputs !== currentInputs;

  const handleCalculate = () => {
    if (!roughCut || !radius) {
      setShowError(true);
      return;
    }

    const roughCutNum = parseFloat(roughCut);
    const radiusNum = parseFloat(radius);

    if (isNaN(roughCutNum) || isNaN(radiusNum)) {
      setShowError(true);
      return;
    }

    setShowError(false);
    setCalculatedInputs(currentInputs);

    // CircWalkway calculation logic from Android app
    // Calculate treads in 90 degrees: ((radius * π) / 2) / roughCut
    const calculatedTreadsIn90 = ((radiusNum * Math.PI) / 2) / roughCutNum;
    
    // Calculate angle per tread: 90 / treadsIn90
    const calculatedAngle = 90 / calculatedTreadsIn90;

    // Round to 2 decimal places
    const roundedTreads = Math.round(calculatedTreadsIn90 * 100) / 100;
    const roundedAngle = Math.round(calculatedAngle * 100) / 100;

    setTreadsIn90(roundedTreads.toString());
    setTreadAngle(roundedAngle.toString());
  };

  const hasResults = treadsIn90 && treadAngle;

  return (
    <Box component="main" id="main-content" tabIndex={-1} sx={{
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #F5F5F5 0%, #E8EAF6 100%)',
      py: { xs: 2, sm: 4 }
    }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
            Calculate Circular Walkway
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Find tread measurements for curved walkways
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Input Form */}
          <Card component="form" noValidate onSubmit={(event) => { event.preventDefault(); handleCalculate(); }} elevation={3} sx={{ flex: 1, minWidth: 0 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Calculate sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Input Values
                </Typography>
              </Box>

              <Stack spacing={3}>
                {showError && (
                  <Alert severity="error">Enter a number in each highlighted field, then calculate again.</Alert>
                )}
                <TextField
                  fullWidth
                  label="Rough Cut (RC)"
                  type="number"
                  value={roughCut}
                  error={showError && (!roughCut || isNaN(parseFloat(roughCut)))}
                  onChange={(e) => setRoughCut(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Width of each tread"
                />
                
                <TextField
                  fullWidth
                  label="Radius"
                  type="number"
                  value={radius}
                  error={showError && (!radius || isNaN(parseFloat(radius)))}
                  onChange={(e) => setRadius(e.target.value)}
                  inputProps={{ step: "0.01", inputMode: 'decimal' }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">inches</InputAdornment>,
                  }}
                  helperText="Radius of the circular walkway"
                />

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  type="submit"
                  startIcon={<Calculate />}
                  sx={{ 
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    background: 'linear-gradient(45deg, #1976D2 30%, #42A5F5 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #0D47A1 30%, #1976D2 90%)',
                    }
                  }}
                >
                  Calculate
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {/* Results */}
          <Card component="section" aria-label="Calculation results" aria-live="polite" aria-atomic="true" elevation={3} sx={{ flex: 1 }}>
            <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Info sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
                  Results
                </Typography>
              </Box>

              {isStale && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Inputs changed. These results are from your previous calculation. Calculate again to update them.
                </Alert>
              )}

              {hasResults ? (
                <Stack spacing={2}>
                  <Box>
                    <Chip 
                      label="Treads in 90°" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Treads in 90\u00b0" }}
                      fullWidth
                      value={`${treadsIn90} treads`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box>
                    <Chip 
                      label="Tread Angle" 
                      color="primary" 
                      size="small" 
                      sx={{ mb: 1 }} 
                    />
                    <TextField
                      inputProps={{ 'aria-label': "Tread Angle" }}
                      fullWidth
                      value={`${treadAngle}°`}
                      InputProps={{
                        readOnly: true,
                      }}
                      variant="outlined"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#eef7f0',
                          color: 'text.primary',
                        }
                      }}
                    />
                  </Box>

                  <Box sx={{ mt: 2, p: 2, bgcolor: '#edf5fc', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.primary">
                      <strong>Formula:</strong> treads = (radius × π ÷ 2) ÷ roughCut, angle = 90° ÷ treads
                    </Typography>
                  </Box>
                </Stack>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 4,
                  color: 'text.secondary'
                }}>
                  <Calculate sx={{ fontSize: 64, opacity: 0.3, mb: 2 }} />
                  <Typography variant="body1">
                    Enter rough cut and radius to calculate walkway dimensions
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Diagram */}
        <Card elevation={3} sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" component="h2" gutterBottom sx={{ fontWeight: 600, textAlign: 'center' }}>
              Reference Diagram
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <ResponsiveDiagram 
              imageName="circ_walkway" 
              alt="Circular walkway diagram showing radius and tread calculations" 
            />
          </CardContent>
        </Card>

      </Container>
    </Box>
  );
};

export default CircWalkway;